import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Shortcut, UserPreferences } from '../types';

export function generateShortcutsPDF(
  shortcuts: Shortcut[], 
  preferences: UserPreferences,
  userEmail: string
) {
  // Filter to only active shortcuts
  const activeShortcuts = shortcuts.filter(
    shortcut => !preferences.disabledShortcuts.includes(shortcut.id)
  );

  // Define shortcuts with long expansions that should go at the bottom
  const longShortcuts = ['bss', 'pci', 'sob', 'ssr', 'wct'];
  
  // Separate shortcuts into regular and long
  const regularShortcuts = activeShortcuts.filter(
    s => !longShortcuts.includes(s.shortcut.toLowerCase())
  );
  const bottomShortcuts = activeShortcuts.filter(
    s => longShortcuts.includes(s.shortcut.toLowerCase())
  );

  // Sort both groups alphabetically
  regularShortcuts.sort((a, b) => a.shortcut.localeCompare(b.shortcut));
  bottomShortcuts.sort((a, b) => a.shortcut.localeCompare(b.shortcut));

  // Create PDF in 4x6 inch size (portrait) with minimal margins for Zebra ZD421
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'in',
    format: [4, 6]
  });

  // Minimal margins for thermal printer (0.1 inch)
  const margin = 0.1;
  const pageWidth = 4;
  const pageHeight = 6;

  // Prepare data for 4-column table
  const tableData: any[][] = [];
  
  // Process regular shortcuts in pairs
  for (let i = 0; i < regularShortcuts.length; i += 2) {
    const left = regularShortcuts[i];
    const right = regularShortcuts[i + 1];
    
    const leftExpansion = left.isTemplate && preferences.customExpansions[left.id]
      ? preferences.customExpansions[left.id]
      : left.expansion;
    
    if (right) {
      const rightExpansion = right.isTemplate && preferences.customExpansions[right.id]
        ? preferences.customExpansions[right.id]
        : right.expansion;
      
      tableData.push([left.shortcut, leftExpansion, right.shortcut, rightExpansion]);
    } else {
      // Odd number of shortcuts - last one gets empty right columns
      tableData.push([left.shortcut, leftExpansion, '', '']);
    }
  }
  
  // Add separator if we have both regular and long shortcuts
  if (regularShortcuts.length > 0 && bottomShortcuts.length > 0) {
    tableData.push(['—', '— Long Expansions —', '—', '—']);
  }
  
  // Add long shortcuts at the bottom
  bottomShortcuts.forEach(shortcut => {
    const expansion = shortcut.isTemplate && preferences.customExpansions[shortcut.id]
      ? preferences.customExpansions[shortcut.id]
      : shortcut.expansion;
    
    // Long shortcuts span all 4 columns
    tableData.push([shortcut.shortcut, expansion, '', '']);
  });

  // Calculate optimal font size based on number of rows needed
  const totalRows = Math.ceil(regularShortcuts.length / 2) + bottomShortcuts.length + (bottomShortcuts.length > 0 ? 1 : 0);
  const printableHeight = pageHeight - (2 * margin);
  
  // More aggressive font sizing to ensure single page
  let baseFontSize = 6.5;
  let cellPadding = 0.02;
  let titleFontSize = 12;
  let dateFontSize = 6;
  let startY = margin + 0.3;
  
  if (totalRows > 40) {
    baseFontSize = 6;
    cellPadding = 0.018;
    titleFontSize = 11;
    dateFontSize = 5.5;
  }
  if (totalRows > 50) {
    baseFontSize = 5.5;
    cellPadding = 0.016;
    titleFontSize = 10;
    dateFontSize = 5;
  }
  if (totalRows > 60) {
    baseFontSize = 5;
    cellPadding = 0.014;
    titleFontSize = 9;
    dateFontSize = 4.5;
    startY = margin + 0.28;
  }
  if (totalRows > 70) {
    baseFontSize = 4.5;
    cellPadding = 0.012;
    titleFontSize = 8;
    dateFontSize = 4;
    startY = margin + 0.25;
  }
  
  // Add title - smaller to save space
  doc.setFontSize(titleFontSize);
  doc.setFont('helvetica', 'bold');
  doc.text('GD Text Expander Shortcuts', pageWidth / 2, margin + 0.15, { align: 'center' });
  
  // Add date in smaller font
  doc.setFontSize(dateFontSize);
  doc.setFont('helvetica', 'normal');
  doc.text(`${new Date().toLocaleDateString()} | ${activeShortcuts.length} shortcuts`, pageWidth / 2, margin + 0.23, { align: 'center' });

  // Add table with shortcuts
  autoTable(doc, {
    head: [['Shortcut', 'Expands To', 'Shortcut', 'Expands To']],
    body: tableData,
    startY: startY,
    margin: { left: margin, right: margin, top: margin + 0.3, bottom: margin },
    styles: {
      fontSize: baseFontSize,
      cellPadding: cellPadding,
      lineWidth: 0,
      lineColor: [255, 255, 255],
      textColor: [0, 0, 0],
      font: 'helvetica',
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [66, 66, 66],
      textColor: [255, 255, 255],
      fontSize: baseFontSize,
      fontStyle: 'bold',
      halign: 'left'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    columnStyles: {
      0: { 
        cellWidth: 0.45,
        fontStyle: 'bold',
        halign: 'left'
      },
      1: { 
        cellWidth: 1.35,
        halign: 'left'
      },
      2: { 
        cellWidth: 0.45,
        fontStyle: 'bold',
        halign: 'left'
      },
      3: { 
        cellWidth: 1.35,
        halign: 'left'
      }
    },
    // Custom styling and cell merging
    didParseCell: function(data: any) {
      // Style separator row
      if (data.cell.raw === '—' || data.cell.raw === '— Long Expansions —') {
        data.cell.styles.fontStyle = 'italic';
        data.cell.styles.textColor = [100, 100, 100];
        data.cell.styles.fontSize = baseFontSize - 1;
        data.cell.styles.halign = 'center';
      }
      
      // For long expansions, check if this is a row with a long shortcut
      const rowData = data.row.raw;
      if (rowData && longShortcuts.includes(rowData[0].toLowerCase()) && data.column.index === 1) {
        // Merge cells for long expansions
        data.cell.colSpan = 3;
      }
    },
    theme: 'grid',
    showHead: 'firstPage',
    pageBreak: 'avoid', // Try to avoid page breaks
    willDrawPage: function(data: any) {
      // Prevent drawing on additional pages for 4x6
      if (data.pageNumber > 1) {
        return false;
      }
    },
    didDrawPage: function(data: any) {
      // Only add page number if there are multiple pages
      const pageCount = (doc as any).internal.getNumberOfPages();
      if (pageCount > 1) {
        doc.setFontSize(6);
        doc.setFont('helvetica', 'italic');
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`, 
          pageWidth / 2, 
          pageHeight - margin / 2, 
          { align: 'center' }
        );
      }
    }
  });

  // Save the PDF
  doc.save(`gd-shortcuts-${new Date().toISOString().split('T')[0]}.pdf`);
}

export function generateShortcutsFullPagePDF(
  shortcuts: Shortcut[], 
  preferences: UserPreferences,
  userEmail: string
) {
  // Filter to only active shortcuts
  const activeShortcuts = shortcuts.filter(
    shortcut => !preferences.disabledShortcuts.includes(shortcut.id)
  );

  // Define shortcuts with long expansions that should go at the bottom
  const longShortcuts = ['bss', 'pci', 'sob', 'ssr', 'wct'];
  
  // Separate shortcuts into regular and long
  const regularShortcuts = activeShortcuts.filter(
    s => !longShortcuts.includes(s.shortcut.toLowerCase())
  );
  const bottomShortcuts = activeShortcuts.filter(
    s => longShortcuts.includes(s.shortcut.toLowerCase())
  );

  // Sort both groups alphabetically
  regularShortcuts.sort((a, b) => a.shortcut.localeCompare(b.shortcut));
  bottomShortcuts.sort((a, b) => a.shortcut.localeCompare(b.shortcut));

  // Create PDF in standard letter size (8.5x11 inch) with 0.25 inch margins
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'in',
    format: 'letter'
  });

  // 0.25 inch margins all around
  const margin = 0.25;
  const pageWidth = 8.5;
  const pageHeight = 11;

  // Prepare data for 4-column table
  const tableData: any[][] = [];
  
  // Process regular shortcuts in pairs
  for (let i = 0; i < regularShortcuts.length; i += 2) {
    const left = regularShortcuts[i];
    const right = regularShortcuts[i + 1];
    
    const leftExpansion = left.isTemplate && preferences.customExpansions[left.id]
      ? preferences.customExpansions[left.id]
      : left.expansion;
    
    if (right) {
      const rightExpansion = right.isTemplate && preferences.customExpansions[right.id]
        ? preferences.customExpansions[right.id]
        : right.expansion;
      
      tableData.push([left.shortcut, leftExpansion, right.shortcut, rightExpansion]);
    } else {
      // Odd number of shortcuts - last one gets empty right columns
      tableData.push([left.shortcut, leftExpansion, '', '']);
    }
  }
  
  // Add separator if we have both regular and long shortcuts
  if (regularShortcuts.length > 0 && bottomShortcuts.length > 0) {
    tableData.push(['—', '— Long Expansions —', '—', '—']);
  }
  
  // Add long shortcuts at the bottom
  bottomShortcuts.forEach(shortcut => {
    const expansion = shortcut.isTemplate && preferences.customExpansions[shortcut.id]
      ? preferences.customExpansions[shortcut.id]
      : shortcut.expansion;
    
    // Long shortcuts span all 4 columns
    tableData.push([shortcut.shortcut, expansion, '', '']);
  });

  // Calculate optimal sizing for full page
  const totalRows = Math.ceil(regularShortcuts.length / 2) + bottomShortcuts.length + (bottomShortcuts.length > 0 ? 1 : 0);
  
  // Dynamic sizing for full page
  let baseFontSize = 10;
  let cellPadding = 0.08;
  let titleFontSize = 18;
  let dateFontSize = 12;
  let startY = margin + 0.7;
  
  if (totalRows > 80) {
    baseFontSize = 9;
    cellPadding = 0.06;
    titleFontSize = 16;
    dateFontSize = 10;
  }
  if (totalRows > 100) {
    baseFontSize = 8;
    cellPadding = 0.05;
    titleFontSize = 14;
    dateFontSize = 9;
  }
  if (totalRows > 120) {
    baseFontSize = 7;
    cellPadding = 0.04;
    titleFontSize = 12;
    dateFontSize = 8;
    startY = margin + 0.6;
  }
  if (totalRows > 140) {
    baseFontSize = 6;
    cellPadding = 0.03;
    titleFontSize = 11;
    dateFontSize = 7;
    startY = margin + 0.5;
  }

  // Add title
  doc.setFontSize(titleFontSize);
  doc.setFont('helvetica', 'bold');
  doc.text('GD Text Expander Shortcuts', pageWidth / 2, margin + 0.3, { align: 'center' });
  
  // Add date
  doc.setFontSize(dateFontSize);
  doc.setFont('helvetica', 'normal');
  doc.text(`${new Date().toLocaleDateString()} | ${activeShortcuts.length} shortcuts`, pageWidth / 2, margin + 0.45, { align: 'center' });

  // Add table with shortcuts
  autoTable(doc, {
    head: [['Shortcut', 'Expands To', 'Shortcut', 'Expands To']],
    body: tableData,
    startY: startY,
    margin: { left: margin, right: margin, top: startY, bottom: margin },
    styles: {
      fontSize: baseFontSize,
      cellPadding: cellPadding,
      lineWidth: 0,
      lineColor: [255, 255, 255],
      textColor: [0, 0, 0],
      font: 'helvetica',
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [66, 66, 66],
      textColor: [255, 255, 255],
      fontSize: baseFontSize + 1,
      fontStyle: 'bold',
      halign: 'left'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    columnStyles: {
      0: { 
        cellWidth: 1.0,
        fontStyle: 'bold',
        halign: 'left'
      },
      1: { 
        cellWidth: 3.0,
        halign: 'left'
      },
      2: { 
        cellWidth: 1.0,
        fontStyle: 'bold',
        halign: 'left'
      },
      3: { 
        cellWidth: 3.0,
        halign: 'left'
      }
    },
    // Custom styling and cell merging
    didParseCell: function(data: any) {
      // Style separator row
      if (data.cell.raw === '—' || data.cell.raw === '— Long Expansions —') {
        data.cell.styles.fontStyle = 'italic';
        data.cell.styles.textColor = [100, 100, 100];
        data.cell.styles.fontSize = baseFontSize - 1;
        data.cell.styles.halign = 'center';
      }
      
      // For long expansions, check if this is a row with a long shortcut
      const rowData = data.row.raw;
      if (rowData && longShortcuts.includes(rowData[0].toLowerCase()) && data.column.index === 1) {
        // Merge cells for long expansions
        data.cell.colSpan = 3;
      }
    },
    theme: 'grid',
    showHead: 'firstPage',
    pageBreak: 'avoid', // Keep everything on one page
    willDrawPage: function(data: any) {
      // Prevent drawing on additional pages
      if (data.pageNumber > 1) {
        return false;
      }
    }
  });

  // Save the PDF
  doc.save(`gd-shortcuts-full-${new Date().toISOString().split('T')[0]}.pdf`);
}